import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { CheckCircle, XCircle, AlertCircle, Info, X } from "lucide-react";

/**
 * Toast — single auto-dismissing notification, styled to the Manager theme.
 *
 * The enter/exit animations used to live in a `<style jsx>` block, which CRA
 * never compiled (styled-jsx is not a dependency): React rendered it as a
 * plain `<style>` tag, so the keyframes and `.animate-slide-*` classes leaked
 * into the global stylesheet. They now come from tailwind.config.js instead.
 *
 * Direction comes from the `dir` attribute the layout sets, so the accent bar
 * (`border-s-4`) and the close button (`end-*`) follow the reading direction
 * with no RTL overrides. `position` names the LTR corner and mirrors under
 * RTL — "top-right" is the trailing corner, i.e. top-left in Arabic — and the
 * slide direction mirrors with it.
 */

const TYPE_CONFIG = {
  success: {
    surface: "bg-accent-50 text-accent-800",
    border: "border-accent-500",
    icon: "text-accent-500",
    ring: "focus:ring-accent-500",
    Icon: CheckCircle,
  },
  error: {
    surface: "bg-red-50 text-red-800",
    border: "border-red-500",
    icon: "text-red-500",
    ring: "focus:ring-red-500",
    Icon: XCircle,
  },
  warning: {
    surface: "bg-sunshine-50 text-sunshine-800",
    border: "border-sunshine-500",
    icon: "text-sunshine-500",
    ring: "focus:ring-sunshine-500",
    Icon: AlertCircle,
  },
  info: {
    surface: "bg-primary-50 text-primary-800",
    border: "border-primary-500",
    icon: "text-primary-500",
    ring: "focus:ring-primary-500",
    Icon: Info,
  },
  danger: {
    surface: "bg-red-50 text-red-800",
    border: "border-red-500",
    icon: "text-red-500",
    ring: "focus:ring-red-500",
    Icon: XCircle,
  },
};

// Corner placement plus the matching slide direction. Centered toasts fade
// instead: a slide keyframe would overwrite the `-translate-x-1/2` that
// centres them.
const POSITIONS = {
  "top-right": {
    place: "top-6 end-6",
    enter: "animate-slide-in-right rtl:animate-slide-in-left",
    exit: "animate-slide-out-right rtl:animate-slide-out-left",
  },
  "top-left": {
    place: "top-6 start-6",
    enter: "animate-slide-in-left rtl:animate-slide-in-right",
    exit: "animate-slide-out-left rtl:animate-slide-out-right",
  },
  "top-center": {
    place: "top-6 left-1/2 -translate-x-1/2",
    enter: "animate-fade-in",
    exit: "animate-fade-out",
  },
  "bottom-right": {
    place: "bottom-6 end-6",
    enter: "animate-slide-in-right rtl:animate-slide-in-left",
    exit: "animate-slide-out-right rtl:animate-slide-out-left",
  },
  "bottom-left": {
    place: "bottom-6 start-6",
    enter: "animate-slide-in-left rtl:animate-slide-in-right",
    exit: "animate-slide-out-left rtl:animate-slide-out-right",
  },
  "bottom-center": {
    place: "bottom-6 left-1/2 -translate-x-1/2",
    enter: "animate-fade-in",
    exit: "animate-fade-out",
  },
};

const EXIT_DURATION = 300;

const Toast = ({
  message,
  type = "success",
  duration = 5000,
  onClose,
  position = "top-right",
}) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(true);
  const [isExiting, setIsExiting] = useState(false);

  const handleClose = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      setIsVisible(false);
      if (onClose) onClose();
    }, EXIT_DURATION);
  }, [onClose]);

  useEffect(() => {
    const timer = setTimeout(handleClose, duration);
    return () => clearTimeout(timer);
  }, [duration, handleClose]);

  if (!isVisible) return null;

  const { surface, border, icon, ring, Icon } =
    TYPE_CONFIG[type] || TYPE_CONFIG.success;
  const { place, enter, exit } = POSITIONS[position] || POSITIONS["top-right"];

  return (
    <div
      className={`fixed ${place} z-[9999] ${isExiting ? exit : enter}`}
      role="alert"
      aria-live="polite"
    >
      <div
        className={`${surface} ${border} flex items-start gap-3 w-80 max-w-[calc(100vw-3rem)] p-4 rounded-2xl shadow-soft-lg border-s-4`}
      >
        <Icon className={`w-5 h-5 shrink-0 mt-0.5 ${icon}`} />

        <p className="flex-1 min-w-0 text-sm font-medium leading-relaxed">
          {message}
        </p>

        <button
          type="button"
          onClick={handleClose}
          aria-label={t("common.close", "Close")}
          className={`shrink-0 -me-1 -mt-1 p-1 rounded-full ${icon} ${ring} hover:opacity-70 transition-opacity focus:outline-none focus:ring-2 focus:ring-offset-2`}
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

Toast.propTypes = {
  message: PropTypes.node.isRequired,
  type: PropTypes.oneOf(["success", "error", "warning", "info", "danger"]),
  duration: PropTypes.number,
  onClose: PropTypes.func,
  position: PropTypes.oneOf(Object.keys(POSITIONS)),
};

export default Toast;
